import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { getClosestContainerNode, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { stripTags } from '../../utils/ids-xss-utils/ids-xss-utils';

import IdsToolbarSection from './ids-toolbar-section';
import './ids-toolbar-more-actions';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-toolbar.scss';
import type IdsToolbarMoreActions from './ids-toolbar-more-actions';
import type IdsSearchField from '../ids-search-field/ids-search-field';

const FORMATTER_VARIANT = 'alternate-formatter';
const TOOLBAR_TYPES = ['formatter'];

interface ToolbarItem extends HTMLElement {
  disabled?: boolean;
  tabIndex: number;
  value?: unknown;
  overflowTarget?: ToolbarItem;
}

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Toolbar Component
 * @type {IdsToolbar}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 */
@customElement('ids-toolbar')
@scss(styles)
export default class IdsToolbar extends Base {
  /**
   * Watches for Toolbar size changes
   */
  #resizeObserver?: ResizeObserver | null;

  /**
   * Watches for DOM changes in tabs
   */
  #mutationObserver?: MutationObserver | null;

  /**
   * Flag to prevent recursive calls during overflow refresh
   */
  #isRefreshing = false;

  constructor() {
    super();
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.PADDING,
      attributes.TABBABLE,
      attributes.TYPE
    ];
  }

  /**
   * Handle attribute changes
   * @param {string} name attribute name
   * @param {string} oldValue old value
   * @param {string} newValue new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === attributes.TYPE) {
      this.onTypeChange(oldValue, newValue);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    // ARIA role is set on the host element (not in the shadow DOM template) because:
    // 1. Screen readers and assistive technologies interact with the light DOM
    // 2. Roles on shadow DOM elements may not be properly exposed to the accessibility tree
    // 3. This follows web components best practices for accessibility
    this.setAttribute('role', 'toolbar');
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
    this.#configureResizeObserver();
    this.#configureMutationObserver();
    this.#setType(null, this.type);
    this.makeTabbable(this.detectTabbable());
  }

  /**
   * Reorders sections after all children are connected to the DOM.
   */
  mountedCallback(): void {
    super.mountedCallback?.();
    this.#reorderSections();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  /**
   * Reorders sections by type and alignment, then sets flex-grow values.
   * @private
   * @returns {void}
   */
  #reorderSections(): void {
    const alignOrder = ['start', 'center', 'center-even', 'end'];
    const allChildren = Array.from(this.children);
    const sections = allChildren.filter((child) => child.tagName === 'IDS-TOOLBAR-SECTION' || child.tagName === 'IDS-TOOLBAR-MORE-ACTIONS') as IdsToolbarSection[];

    const titleSections: IdsToolbarSection[] = [];
    const buttonsetSections: Map<string, IdsToolbarSection[]> = new Map();
    const reorderableSections: IdsToolbarSection[] = [];
    const preservedSections: Map<number, IdsToolbarSection> = new Map();

    sections.forEach((section, index) => {
      const sectionEl = section as IdsToolbarSection;

      // Preserve sections that shouldn't be reordered
      if (sectionEl.type === 'button' || sectionEl.type === 'search' || sectionEl.type === 'fluid' || sectionEl.type === 'static') {
        preservedSections.set(index, sectionEl);
        return;
      }

      if (section.tagName === 'IDS-TOOLBAR-MORE-ACTIONS') {
        preservedSections.set(index, sectionEl);
        return;
      }

      if (sectionEl.type === 'title') {
        titleSections.push(sectionEl);
        reorderableSections.push(sectionEl);
      } else if (sectionEl.type === 'buttonset') {
        const align = sectionEl.align || 'start';
        if (!buttonsetSections.has(align)) {
          buttonsetSections.set(align, []);
        }
        buttonsetSections.get(align)!.push(sectionEl);
        reorderableSections.push(sectionEl);
      }
    });

    // Only reorder if there are reorderable sections
    if (reorderableSections.length > 0) {
      const orderedReorderable: IdsToolbarSection[] = [
        ...titleSections,
        ...alignOrder.flatMap((align) => buttonsetSections.get(align) || [])
      ];

      let needsReorder = false;
      let reorderableIndex = 0;
      for (let i = 0; i < sections.length; i++) {
        if (!preservedSections.has(i)) {
          if (sections[i] !== orderedReorderable[reorderableIndex]) {
            needsReorder = true;
            break;
          }
          reorderableIndex++;
        }
      }

      if (needsReorder) {
        // Rebuild the full order: preserved sections stay in place, reorderable sections get reordered
        const newOrder: IdsToolbarSection[] = [];
        let reorderableIdx = 0;
        for (let i = 0; i < sections.length; i++) {
          if (preservedSections.has(i)) {
            newOrder.push(preservedSections.get(i)!);
          } else {
            newOrder.push(orderedReorderable[reorderableIdx++]);
          }
        }
        newOrder.forEach((section) => {
          this.appendChild(section);
        });
      }
    }

    // Auto-set flex-grow for grouping consecutive sections
    // Get the current sections after reordering
    const currentSections = Array.from(this.children).filter((child) => child.tagName === 'IDS-TOOLBAR-SECTION' || child.tagName === 'IDS-TOOLBAR-MORE-ACTIONS') as IdsToolbarSection[];
    this.#autoSetFlexGrow(currentSections);
  }

  /**
   * Sets flex-grow values on sections based on type, alignment, and position.
   * @private
   * @param {IdsToolbarSection[]} sections - The toolbar sections to process
   * @returns {void}
   */
  #autoSetFlexGrow(sections: IdsToolbarSection[]): void {
    const groupMap: Map<string, IdsToolbarSection[]> = new Map();
    let hasEndSections = false;
    let firstEndSection: IdsToolbarSection | null = null;
    let lastEndSection: IdsToolbarSection | null = null;
    let nextSectionAfterSearch: IdsToolbarSection | null = null;

    sections.forEach((section, index) => {
      if (section.tagName !== 'IDS-TOOLBAR-SECTION') return;

      if (section.hasAttribute('auto-push-end')) {
        section.removeAttribute('auto-push-end');
      }

      section.removeAttribute(attributes.FLEX_SHRINK);

      if (section.hasAttribute(attributes.FLEX_GROW)) return;
      if (section.type === 'fluid' || section.type === 'button') return;

      if (section.type === 'search') {
        for (let i = index + 1; i < sections.length; i++) {
          if (sections[i].tagName === 'IDS-TOOLBAR-SECTION' && sections[i].type !== 'search') {
            nextSectionAfterSearch = sections[i];
            break;
          }
        }
        return;
      }

      const key = this.#getGroupKey(section);
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(section);

      if (section.type === 'buttonset' && section.align === 'end') {
        hasEndSections = true;
        if (!firstEndSection) firstEndSection = section;
        lastEndSection = section;
      }
    });

    const hasMultipleEndSections = (groupMap.get('buttonset:end') || []).length >= 2;
    const hasMultipleTitleSections = (groupMap.get('title:start') || []).length >= 2;
    const hasMultipleStartSections = (groupMap.get('buttonset:start') || []).length >= 2;
    const hasDuplicateGroups = hasMultipleEndSections || hasMultipleTitleSections || hasMultipleStartSections;

    if (firstEndSection && hasDuplicateGroups) {
      (firstEndSection as IdsToolbarSection).setAttribute('auto-push-end', '');
    }

    sections.forEach((section, index) => {
      if (section.tagName !== 'IDS-TOOLBAR-SECTION') return;
      if (section.hasAttribute(attributes.FLEX_GROW)) return;
      if (section.type === 'fluid' || section.type === 'search' || section.type === 'button') return;

      // Check if the next section is centered - if so, this section should have flex-grow="0"
      const nextSection = index + 1 < sections.length ? sections[index + 1] : null;
      if (nextSection && nextSection.tagName === 'IDS-TOOLBAR-SECTION' && nextSection.align === 'center') {
        section.setAttribute(attributes.FLEX_GROW, '0');
        return;
      }

      // Check if the next section is empty - if so, this section should have flex-grow="1"
      if (nextSection && nextSection.tagName === 'IDS-TOOLBAR-SECTION' && nextSection.classList.contains('empty')) {
        section.setAttribute(attributes.FLEX_GROW, '1');
        return;
      }

      // Check if there's a right-aligned (end) buttonset section after this one
      // If so, and this is the last non-end section, give it flex-grow="1" to push end sections right
      let hasEndSectionAfter = false;
      for (let i = index + 1; i < sections.length; i++) {
        if (sections[i].tagName === 'IDS-TOOLBAR-SECTION' && sections[i].type === 'buttonset' && sections[i].align === 'end') {
          hasEndSectionAfter = true;
          break;
        }
      }

      if (hasEndSectionAfter && (section.type === 'title' || section.type === 'static' || (section.type === 'buttonset'))) {
        // Check if this is the last non-end section before the end sections
        let isLastBeforeEnd = true;
        for (let i = index + 1; i < sections.length; i++) {
          const nextSec = sections[i];
          if (nextSec.tagName !== 'IDS-TOOLBAR-SECTION') continue;
          if (nextSec.type === 'buttonset' && nextSec.align === 'end') break;
          if (nextSec.type !== 'button' && nextSec.type !== 'search' && nextSec.type !== 'fluid') {
            isLastBeforeEnd = false;
            break;
          }
        }

        if (isLastBeforeEnd) {
          section.setAttribute(attributes.FLEX_SHRINK, '');
          section.setAttribute(attributes.FLEX_GROW, '1');
          return;
        }
      }

      const key = this.#getGroupKey(section);
      const group = groupMap.get(key) || [];

      if (group.length < 2) return;

      const isLastEnd = hasEndSections && section === lastEndSection;
      const isLastInGroup = group[group.length - 1] === section;
      const isNextAfterSearch = section === nextSectionAfterSearch;

      if (hasEndSections && hasMultipleEndSections && section.type === 'buttonset' && section.align === 'end') {
        section.setAttribute(attributes.FLEX_GROW, '0');
        return;
      }

      if (hasEndSections && hasMultipleTitleSections && (section.type === 'title' || section.type === 'static')) {
        section.setAttribute(attributes.FLEX_GROW, '0');
        return;
      }

      if (isLastEnd || isNextAfterSearch) {
        section.setAttribute(attributes.FLEX_GROW, '0');
        return;
      }

      section.setAttribute(attributes.FLEX_GROW, isLastInGroup ? '1' : '0');
      section.toggleAttribute(attributes.FLEX_SHRINK, isLastInGroup);
    });
  }

  /**
   * Generates a grouping key for a section in format "type:align".
   * @private
   * @param {IdsToolbarSection} section - The section to generate a key for
   * @returns {string} Key in format "type:align" (e.g., "buttonset:end")
   */
  #getGroupKey(section: IdsToolbarSection): string {
    const type = section.type || 'default';
    const align = section.align || 'start';
    return `${type}:${align}`;
  }

  /**
   * Configures the resize observer.
   * @private
   */
  #configureResizeObserver(): void {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#resize();
    });
    this.#resizeObserver.observe(this);
  }

  /**
   * Configures the mutation observer for toolbar content changes.
   * @private
   */
  #configureMutationObserver(): void {
    this.#mutationObserver = new MutationObserver((mutations) => {
      // Prevent recursive calls during refresh operations
      if (this.#isRefreshing) return;

      // Filter out mutations that occur within ids-toolbar-more-actions to avoid infinite loops
      const relevantMutations = mutations.filter((mutation) => {
        const target = mutation.target as Element;
        // Ignore mutations inside the more-actions component
        return !target.closest('ids-toolbar-more-actions');
      });

      if (relevantMutations.length === 0) return;

      const hasNewButtons = relevantMutations.some((mutation) => mutation.type === 'childList'
        && mutation.addedNodes.length > 0
        && Array.from(mutation.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE
          && (node as Element).matches?.('ids-button, ids-menu-button')));

      const hasTabChanges = relevantMutations.some((mutation) => mutation.type === 'childList');

      // Check for menu content changes (menu items added/removed) in toolbar buttons' menus
      const hasMenuContentChanges = relevantMutations.some((mutation) => mutation.type === 'childList'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-popup-menu, ids-menu-group')
        // Only respond to menu changes for menu buttons in toolbar sections (not in more-actions)
        && mutation.target.closest('ids-toolbar-section'));

      // Check for section changes (added/removed/reordered)
      const hasSectionChanges = relevantMutations.some((mutation) => mutation.type === 'childList'
        && (Array.from(mutation.addedNodes).some((node: any) => node.nodeType === Node.ELEMENT_NODE
          && node.tagName === 'IDS-TOOLBAR-SECTION')
          || Array.from(mutation.removedNodes).some((node: any) => node.nodeType === Node.ELEMENT_NODE
            && node.tagName === 'IDS-TOOLBAR-SECTION')));

      const hasBasicAttributeChanges = relevantMutations.some((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && ['hidden', 'disabled', 'overflow'].includes(mutation.attributeName || ''));

      // Extract style changes separately for special handling
      const styleAttributeChanges = relevantMutations.filter((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && mutation.attributeName === 'style');

      // Check for attributes that need syncing to overflow items
      const syncableAttributeChanges = relevantMutations.filter((mutation) => mutation.type === 'attributes'
        && mutation.target instanceof Element
        && mutation.target.matches?.('ids-button, ids-menu-button')
        && ['icon', 'text', 'appearance', 'color-variant'].includes(mutation.attributeName || ''));

      if (hasSectionChanges) {
        // Reorder sections and update flex-grow when sections change
        this.#reorderSections();
        this.rebuildOverflowMenu();
      } else if (hasNewButtons) {
        this.rebuildOverflowMenu();
      } else if (hasMenuContentChanges) {
        // Rebuild overflow menu when menu content changes
        this.rebuildOverflowMenu();
      } else if (styleAttributeChanges.length > 0) {
        // Style changes that affect display need to rebuild the overflow menu
        // because items with display:none are filtered from the items list
        this.rebuildOverflowMenu();
      } else if (syncableAttributeChanges.length > 0) {
        // Sync specific attributes to overflow items
        const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
        if (moreActions) {
          syncableAttributeChanges.forEach((mutation) => {
            const button = mutation.target as HTMLElement;
            const attributeName = mutation.attributeName || '';
            const newValue = button.getAttribute(attributeName);
            moreActions.syncAttributeToOverflow(button, attributeName, newValue);
          });
        }
      } else if (hasTabChanges || hasBasicAttributeChanges) {
        this.refreshOverflowedItems();
      }
    });

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'disabled', 'overflow', 'icon', 'text', 'appearance', 'color-variant', 'style']
    });
  }

  /**
   * Sets up event handlers assigned to the Toolbar and its child elements
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    // Translate click events on buttons into Toolbar `selected` events to correspond
    // to the behavior of menu buttons and the "More Actions" menu
    this.onEvent('click.toolbar-item', this, (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('ids-menu-button, ids-button') as ToolbarItem | null;
      if (btn) {
        this.triggerSelectedEvent(btn);
        this.triggerEvent('click', this, {
          bubbles: true,
          detail: { elem: btn }
        });
      }
    });

    const searchfield = this.querySelector<IdsSearchField>('ids-search-field');
    const toggleExpanded = (shouldExpand: boolean) => {
      this.sections.forEach((section: IdsToolbarSection) => {
        const shadowSection = section.shadowRoot?.querySelector('.ids-toolbar-section');

        if (shouldExpand) {
          shadowSection?.classList.add('searchfield-expanded');
          section?.classList.add('searchfield-expanded');
        } else {
          shadowSection?.classList.remove('searchfield-expanded');
          section?.classList.remove('searchfield-expanded');
        }
      });
    };

    if (searchfield && searchfield?.onEvent) {
      searchfield?.onEvent('expand', searchfield, () => {
        toggleExpanded(true);
      });

      searchfield?.onEvent('collapse', searchfield, () => {
        toggleExpanded(false);
      });
    }
  }

  /**
   * Sets up the connection to the global keyboard handler
   * @private
   * @returns {void}
   */
  #attachKeyboardListeners(): void {
    // Arrow Up navigates focus backward
    this.listen(['ArrowLeft'], this, (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      // If the target is within a search field, allow normal cursor movement
      if (target.closest('ids-search-field')) {
        return;
      }

      // If the target is within a segmented control, let it handle navigation entirely.
      // Only take over when focus is on the first button (boundary).
      const segmented = target.closest('ids-segmented-control');
      if (segmented) {
        const buttons = Array.from(segmented.querySelectorAll('ids-toggle-button')) as any[];
        const focusedIndex = buttons.findIndex((b: any) => b === document.activeElement);
        if (focusedIndex !== 0) return;
      }

      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if ((target as HTMLElement & { name?: string }).name === 'ids-menu-item') {
        return;
      }
      this.navigate(-1, true);
    });

    // Arrow Right navigates focus forward
    this.listen(['ArrowRight'], this, (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      // If the target is within a search field, allow normal cursor movement
      if (target.closest('ids-search-field')) {
        return;
      }

      // If the target is within a segmented control, let it handle navigation entirely.
      // Only take over when focus is on the last button (boundary).
      const segmented = target.closest('ids-segmented-control');
      if (segmented) {
        const buttons = Array.from(segmented.querySelectorAll('ids-toggle-button')) as any[];
        const focusedIndex = buttons.findIndex((b: any) => b === document.activeElement);
        if (focusedIndex !== buttons.length - 1) return;
      }

      e.preventDefault();
      e.stopPropagation();

      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if ((target as HTMLElement & { name?: string }).name === 'ids-menu-item') {
        return;
      }
      this.navigate(1, true);
    });

    // Arrow Up acts as Left Arrow (navigates backward)
    this.listen(['ArrowUp'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.target as HTMLElement;
      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if ((target as HTMLElement & { name?: string }).name === 'ids-menu-item') {
        return;
      }
      this.navigate(-1, true);
    });

    // Arrow Down acts as Right Arrow (navigates forward)
    this.listen(['ArrowDown'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.target as HTMLElement;
      // If the target is a menu item, either a menu or actions button is currently open
      // with its children focused, and navigation shouldn't continue
      if ((target as HTMLElement & { name?: string }).name === 'ids-menu-item') {
        return;
      }
      this.navigate(1, true);
    });
  }

  /**
   * Uses a currently-highlighted toolbar item to "navigate" a specified number
   * of steps to another toolbar item, highlighting it.
   * @param {number} [amt] the amount of items to navigate
   * @param {boolean} [doFocus] if true, causes the new item to become focused.
   * @returns {HTMLElement} the item that will be focused
   */
  navigate(amt = 0, doFocus = false): ToolbarItem {
    const items = this.items;
    let currentItem = this.focused || items[0];

    if (typeof amt !== 'number') {
      return currentItem;
    }

    // Calculate steps/meta
    const negative = amt < 0;
    let steps = Math.abs(amt);
    let currentIndex = items.indexOf(currentItem);

    // Step through items to the target
    while (steps > 0) {
      currentItem = items[currentIndex + (negative ? -1 : 1)];
      currentIndex = items.indexOf(currentItem);

      // "-1" means we've crossed the boundary and need to loop back around
      if (currentIndex < 0) {
        currentIndex = (negative ? items.length - 1 : 0);
        currentItem = items[currentIndex];
      }

      // Don't count disabled/overflowed items as "taking a step"
      if (!currentItem.disabled && !currentItem.hasAttribute(attributes.OVERFLOWED)) {
        steps -= 1;
      }
    }

    if (!currentItem.disabled && doFocus) {
      this.makeTabbable(currentItem);
      // For segmented controls, enter from the correct end based on direction
      if (currentItem.tagName?.toLowerCase() === 'ids-segmented-control') {
        const buttons = Array.from(currentItem.querySelectorAll('ids-toggle-button')) as HTMLElement[];
        const entryButton = negative ? buttons[buttons.length - 1] : buttons[0];
        (entryButton as HTMLElement)?.focus();
      } else {
        currentItem.focus();
      }
    }

    return currentItem;
  }

  template(): string {
    return `<div class="ids-toolbar">
      <slot></slot>
    </div>`;
  }

  /**
   * Set all buttons to disabled on the toolbar
   * @param {boolean | string} val sets the disabled state of the entire toolbar
   */
  set disabled(val: boolean) {
    const trueVal = stringToBool(val);
    this.toggleAttribute(attributes.DISABLED, trueVal);
    this.container?.classList.toggle(attributes.DISABLED, trueVal);

    // Set disabled state on all relevant subcomponents
    const setDisabledState = (elem: HTMLElement & { disabled?: boolean }) => {
      const elemClassList = Array.from(elem.classList || []);

      if (elemClassList.includes('more-actions-button')) {
        const trueElem = getClosest(elem, 'ids-toolbar-more-actions') as IdsToolbarMoreActions;
        trueElem.disabled = trueVal;
      } else {
        elem.disabled = trueVal;
      }
    };
    this.items.forEach(setDisabledState);
    (this.textElems as HTMLElement[]).forEach(setDisabledState);
    this.refreshOverflowedItems();
  }

  /**
   * @returns {boolean} true if the toolbar is currently disabled
   */
  get disabled(): boolean {
    // Fallback to attribute if container not available yet
    if (!this.container) {
      return this.hasAttribute(attributes.DISABLED);
    }
    return !!this.container?.classList?.contains(attributes.DISABLED);
  }

  /**
   * Refreshes the overflow state of toolbar items
   * @returns {void}
   */
  refreshOverflowedItems(): void {
    if (this.#isRefreshing) return;

    this.#isRefreshing = true;
    try {
      const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
      if (moreActions) {
        moreActions.refreshOverflowedItems();
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Pause the mutation observer to prevent rebuilds during beforeShow
   */
  pauseMutationObserver(): void {
    this.#mutationObserver?.disconnect();
  }

  /**
   * Resume the mutation observer after beforeShow completes
   */
  resumeMutationObserver(): void {
    if (this.#mutationObserver) {
      this.#mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'disabled', 'overflow', 'icon', 'text', 'appearance', 'color-variant', 'style']
      });
    }
  }

  /**
   * Refreshes the overflow text of toolbar items
   * @returns {void}
   */
  rebuildOverflowMenu(): void {
    if (this.#isRefreshing) return;

    this.#isRefreshing = true;
    try {
      const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
      if (moreActions) {
        moreActions.rebuildOverflowMenu();
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Gets the buttons in the toolbar
   * @returns {Array} Array of ids-buttons
   */
  get buttons(): Array<HTMLElement> {
    return [...this.querySelectorAll('ids-button')].filter(
      (button) => (button as HTMLElement).style.display !== 'none'
    ) as HTMLElement[];
  }

  /**
   * @readonly
   * @returns {any | undefined} the currently focused menu item, if one exists
   */
  get focused(): ToolbarItem | undefined {
    return this.items.find((item) => {
      const containerNode = getClosestContainerNode(item) as Document | ShadowRoot;
      const focusedEl = containerNode?.activeElement;
      const isEqualNode = focusedEl?.isEqualNode(item);
      // Also match when focus is on a button inside a segmented control
      const isInsideSegmented = item.tagName?.toLowerCase() === 'ids-segmented-control'
        && item.contains(document.activeElement);
      return isEqualNode || isInsideSegmented;
    });
  }

  /**
   * @readonly
   * @returns {Array<ToolbarItem>} list of all available toolbar items present in all toolbar sections
   */
  get items(): Array<ToolbarItem> {
    const i: ToolbarItem[] = [];
    this.sections.forEach((section: any) => {
      // Pass along the More Actions button, if applicable
      if (section?.name === 'ids-toolbar-more-actions') {
        i.push(section.button);
      } else if (section.items) {
        i.push(...section.items);
      }
    });
    return i;
  }

  /**
   * @readonly
   * @returns {Array<Element>} list of all available text nodes present in all toolbar sections
   */
  get textElems(): Array<Element> {
    const i: Array<Element> = [];
    this.sections.forEach((section) => {
      if (section?.name !== 'ids-toolbar-more-actions' && 'textElems' in section) {
        i.push(...section.textElems);
      }
    });
    return i;
  }

  /**
   * @readonly
   * @returns {Array<Element>} list of all available ids-separator nodes present in all toolbar sections
   */
  get separators(): Array<Element> {
    const sep: Array<Element> = [];
    this.sections.forEach((section) => {
      if (section?.name !== 'ids-toolbar-more-actions' && 'separators' in section) {
        sep.push(...section.separators);
      }
    });
    return sep;
  }

  /**
   * @readonly
   * @returns {Array<HTMLElement>} list of available sections within the toolbar
   */
  get sections(): Array<any> {
    return [...this.children].filter((e) => e.matches('ids-toolbar-section, ids-toolbar-more-actions'));
  }

  /**
   * If true, sets the Toolbar mode to allow ALL items to have a usable tabIndex.
   * Default is false, which means one Toolbar element is focusable at a time.
   * @param {boolean | string} val sets the tabbable state of the toolbar
   */
  set tabbable(val: boolean | string) {
    const trueVal = stringToBool(val);
    this.toggleAttribute(attributes.TABBABLE, trueVal);
    this.container?.classList.toggle(attributes.TABBABLE, trueVal);

    // Try to use a currently-focused element
    this.makeTabbable(this.focused);
  }

  /**
   * @returns {boolean} true if the toolbar is fully tabbable
   */
  get tabbable(): boolean {
    return !!this.container?.classList.contains(attributes.TABBABLE);
  }

  /**
   * @returns {string | null} the toolbar type
   */
  get type(): string | null {
    return this.getAttribute(attributes.TYPE);
  }

  /**
   * Set the toolbar type
   * @param {string | null} value toolbar type
   */
  set type(value: string | null) {
    const currentAttr = this.getAttribute(attributes.TYPE);
    const newValue = (typeof value === 'string' && value !== '' && TOOLBAR_TYPES.includes(value))
      ? String(stripTags(value, ''))
      : null;

    if (currentAttr !== newValue) {
      if (newValue) {
        this.setAttribute(attributes.TYPE, newValue);
      } else {
        this.removeAttribute(attributes.TYPE);
      }
    }
  }

  /**
   * Handle type attribute changes
   * @param {string | null} oldValue previous type value
   * @param {string | null} newValue new type value
   */
  onTypeChange(oldValue: string | null, newValue: string | null): void {
    const validNew = (newValue && TOOLBAR_TYPES.includes(newValue)) ? newValue : null;
    const validOld = (oldValue && TOOLBAR_TYPES.includes(oldValue)) ? oldValue : null;
    this.#setType(validOld, validNew);
  }

  /**
   * If set to number the container will have padding added (in pixels)
   * @param {string | number} value sets the padding to the container
   */
  set padding(value: string | number | null) {
    if (value !== null) {
      this.setAttribute(attributes.PADDING, value.toString());
      this.container?.style.setProperty('padding-bottom', `${value}px`);
    } else {
      this.removeAttribute(attributes.PADDING);
      this.container?.style.removeProperty('padding-bottom');
    }
  }

  /**
   * @returns {string | null} the current padding value
   */
  get padding(): string | null {
    return this.getAttribute(attributes.PADDING);
  }

  /**
   * Set the toolbar type to each section
   * @private
   * @param {string|null} oldType the type class to remove
   * @param {string|null} newType the type class to add
   * @returns {void}
   */
  #setType(oldType: string | null, newType: string | null) {
    const cl = this.container?.classList;

    // Update CSS Class for main Toolbar type
    if (oldType) cl?.remove(`type-${oldType}`);
    if (newType) cl?.add(`type-${newType}`);

    // If using a "formatter" type, change the buttons/separators/etc to use the alternate style
    this.sections.forEach((s) => {
      if (newType) {
        s.setAttribute(attributes.TOOLBAR_TYPE, newType);
      } else {
        s.removeAttribute(attributes.TOOLBAR_TYPE);
      }
      if (s.tagName === 'IDS-TOOLBAR-MORE-ACTIONS') {
        if (newType === 'formatter') {
          s.colorVariant = FORMATTER_VARIANT;
        } else {
          s.colorVariant = null;
        }
      }
    });
    [...this.items, ...this.separators].forEach((item) => {
      if (newType === 'formatter') {
        item.setAttribute(attributes.COLOR_VARIANT, FORMATTER_VARIANT);
      } else {
        const val = item.getAttribute(attributes.COLOR_VARIANT);
        if (val === FORMATTER_VARIANT) item.removeAttribute(attributes.COLOR_VARIANT);
      }
    });
  }

  /**
   * Gets the current item that should be used as the "tabbable" item
   * (item that receives focus when the toolbar overall is "focused").
   * @returns {HTMLElement | undefined} an element that currently has a usable tabIndex attribute
   */
  detectTabbable() {
    let tabbableItem;
    for (let i = 0; !tabbableItem && i < this.items.length; i++) {
      if (this.items[i].tabIndex > -1) {
        tabbableItem = this.items[i];
      }
    }
    return tabbableItem;
  }

  /**
   * @private
   * @param {HTMLElement} elem an element residing within the toolbar that can accept
   */
  makeTabbable(elem = this.items[0]) {
    const isTabbable = this.tabbable;
    this.items.forEach((item: HTMLElement) => {
      const nonTabbableTargetIndex = elem.isEqualNode(item) ? 0 : -1;
      item.tabIndex = isTabbable ? 0 : nonTabbableTargetIndex;
    });
  }

  #resize() {
    this.refreshOverflowedItems();
  }

  /**
   * Triggers a `selected` event on a specified Toolbar item
   * @param {ToolbarItem} elem the specified Toolbar item
   * @param {boolean} [triggeredFromOverflow] if true, notifies the event handler that this
   *  `selected` event originated from the Overflow menu
   * @returns {void}
   */
  triggerSelectedEvent(elem: ToolbarItem | null, triggeredFromOverflow = false): void {
    if (!elem) {
      return;
    }

    const detail: {
      elem: ToolbarItem;
      value?: unknown;
      overflowMenuItem?: ToolbarItem;
      triggeredFromOverflow?: boolean;
    } = {
      elem,
      value: elem.value
    };

    if (elem.overflowTarget) {
      detail.elem = elem.overflowTarget;
      detail.value = elem.overflowTarget.value;
      detail.overflowMenuItem = elem;
      detail.triggeredFromOverflow = triggeredFromOverflow;
    }

    // Dispatch on targetElement only for overflow items, otherwise use elem
    const eventTarget = elem.overflowTarget ? this : elem;

    eventTarget.dispatchEvent(new CustomEvent('selected', {
      bubbles: true,
      detail
    }));
  }
}
